import { resources } from '@/i18n'
import { FALLBACK_LANGUAGE, isLanguage, languageFromTags, type Language } from '@repo/constants'
import type { i18n as I18n } from 'i18next'
import { initReactI18next } from 'react-i18next'

/**
 * An explicit pick wins; without one the portal follows the browser. Stored
 * values come from localStorage, so a stale or hand-edited one is ignored.
 */
export const resolveLocale = (
  stored: unknown,
  browserTags: readonly (string | undefined)[],
): Language => (isLanguage(stored) ? stored : languageFromTags(browserTags))

type DocumentLike = { documentElement: { lang: string }; title: string }

/**
 * `<html lang>` must name the language the page is rendered in. When it does
 * not, Chrome offers to auto-translate, and its DOM rewrites crash React with
 * insertBefore errors on the next re-render. The listener is registered before
 * `init` so the first `languageChanged` (emitted by `init` itself) sets it too.
 */
export const initI18n = (
  instance: I18n,
  { language, document }: { language: Language; document: DocumentLike },
) => {
  instance.on('languageChanged', (lng) => {
    document.documentElement.lang = lng
    document.title = instance.t('common:brand.portalName')
  })
  return instance.use(initReactI18next).init({
    lng: language,
    resources,
    fallbackLng: FALLBACK_LANGUAGE,
    interpolation: {
      escapeValue: false,
    },
  })
}
